import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ValidatorsEx } from '../../../../validators/validators-ex';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { IconButtonComponent } from '../../../core/buttons/icon-button.component';
import { DatePickerComponent } from '../../../core/controls/date-picker.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RadioButtonComponent } from '../../../core/controls/radio-button.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { ReportsService } from '../../../../services/reports/reports.service';
import { ClientFieldFilter } from '../../../../models/api/reports/client-field-filter';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-client-field-criteria',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    ActionRowComponent,
    ButtonComponent,
    SelectComponent,
    InputComponent,
    DatePickerComponent,
    RadioButtonComponent,
    IconButtonComponent,
  ],
  template: `
    <div class="tw-pt-0">
      <div class="tw-pb-6 tw-pt-2">
        <app-action-row>
          <app-button
            left1
            label="Add"
            emphasis="Low"
            (click)="addNewFieldCriteria()"
          />
          <app-button
            left2
            label="Remove all"
            emphasis="Low"
            (click)="deleteAll()"
          />
        </app-action-row>
      </div>
      @for (
        form of fgroups.controls.criterias.controls;
        track form;
        let i = $index
      ) {
        @let fg = $any(form);
        @if (showField) {
          <div
            class="tw-flex tw-items-start tw-gap-2 tw-pb-4 app-fade-in tw-w-full tw-min-w-0"
          >
            <div
              class="tw-flex tw-flex-col md:tw-flex-row tw-gap-2 tw-flex-1 md:tw-flex-initial tw-min-w-0"
            >
              <app-select
                [control]="fg.controls.fieldId"
                label="Field"
                optionLabel="displayName"
                optionValue="id"
                [options]="getAvailableFields(i)"
                class="tw-w-full md:tw-w-[300px] md:tw-min-w-0"
                [showClear]="false"
              ></app-select>
              <app-select
                [control]="fg.controls.operator"
                label="Operator"
                optionLabel="label"
                optionValue="value"
                [options]="fg.controls.operatorOptions.value"
                class="tw-w-full md:tw-w-[150px] md:tw-min-w-0"
                [showHelpText]="false"
                [showClear]="false"
                [virtualScroll]="false"
                [filter]="false"
                [disabled]="fg.controls.operator.disabled"
              ></app-select>
              @if (!fg.controls.operator.value) {
                <app-input
                  [control]="fg.controls.empty"
                  label="Value"
                  class="tw-w-full md:tw-w-[200px] md:tw-min-w-0"
                />
              } @else if (
                fg.controls.operator.value === 'ISNULL' ||
                fg.controls.operator.value === 'ISNOTNULL'
              ) {
                <!-- No value field needed for null operators -->
              } @else {
                @switch (fg.controls.fieldType.value.toLowerCase()) {
                  @case ('character') {
                    @if (
                      fg.controls.isLookup.value &&
                      fg.controls.lookupOptions.value.length
                    ) {
                      <app-select
                        [control]="fg.controls.valueText"
                        optionLabel="label"
                        optionValue="value"
                        label="Value"
                        [options]="fg.controls.lookupOptions.value"
                        class="tw-w-full md:tw-w-[300px] md:tw-min-w-0"
                        [showClear]="false"
                        [virtualScroll]="true"
                        [multiple]="true"
                        [multipleFilter]="true"
                        [chip]="true"
                        [loading]="lookupLoading"
                      />
                    } @else {
                      <app-input
                        [control]="fg.controls.valueText"
                        label="Text"
                        class="tw-w-full md:tw-w-[200px] md:tw-min-w-0"
                      />
                    }
                  }
                  @case ('integer') {
                    <app-input
                      [control]="fg.controls.valueInteger"
                      label="Number"
                      type="Integer"
                      class="tw-w-full md:tw-w-[200px] md:tw-min-w-0"
                    />
                    @if (fg.controls.operator.value === 'BETWEEN') {
                      <app-input
                        [control]="fg.controls.valueIntegerTo"
                        label="To"
                        type="Integer"
                        class="tw-w-full md:tw-w-[200px] md:tw-min-w-0"
                      />
                    }
                  }
                  @case ('decimal') {
                    <app-input
                      [control]="fg.controls.valueDecimal"
                      label="Decimal"
                      type="Decimal"
                      class="tw-w-full md:tw-w-[200px] md:tw-min-w-0"
                    />
                    @if (fg.controls.operator.value === 'BETWEEN') {
                      <app-input
                        [control]="fg.controls.valueDecimalTo"
                        label="To"
                        type="Decimal"
                        class="tw-w-full md:tw-w-[200px] md:tw-min-w-0"
                      />
                    }
                  }
                  @case ('date') {
                    <app-date-picker
                      [control]="fg.controls.valueDateFrom"
                      label="From"
                      class="tw-w-full md:tw-w-[200px] md:tw-min-w-0"
                    />

                    @if (fg.controls.operator.value === 'BETWEEN') {
                      <app-date-picker
                        [control]="fg.controls.valueDateTo"
                        label="To"
                        class="tw-w-full md:tw-w-[200px] md:tw-min-w-0"
                      />
                    }
                  }
                  @case ('logical') {
                    <app-radio-button
                      [control]="fg.controls.valueBoolean"
                      screenReaderLabel="Value"
                      optionLabel="label"
                      optionValue="value"
                      [options]="booleanOptions"
                      [vertical]="false"
                      class="tw-w-full md:tw-w-[200px] md:tw-min-w-0 tw-pt-1"
                    ></app-radio-button>
                  }
                }
              }
            </div>
            @if (i >= 0) {
              <app-icon-button
                class="tw-shrink-0 tw-mt-1"
                icon="fa-light fa-xmark"
                (click)="removeFieldCriteria(i)"
                ariaLabel="Remove client field criteria"
              />
            }
          </div>
        }
      }
    </div>
  `,
  styles: `
    :host ::ng-deep app-select,
    :host ::ng-deep app-input,
    :host ::ng-deep app-date-picker {
      display: block;
      min-width: 0;
    }
    :host ::ng-deep .p-select-label > .option-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
})
export class ClientFieldCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  @Input({ required: true })
  section!: FilterSection;

  private reports = inject(ReportsService);

  showField = false;
  loading = false;
  lookupLoading = true;
  private contractNumber: number;

  booleanOptions: any[] = [
    { label: 'Yes', value: 'Y' },
    { label: 'No', value: 'N' },
  ];

  private readonly fieldConfig: Record<
    string,
    { valueControl: string; toControl?: string; dataType: string }
  > = {
    character: { valueControl: 'valueText', dataType: 'VARCHAR2' },
    integer: {
      valueControl: 'valueInteger',
      toControl: 'valueIntegerTo',
      dataType: 'NUMBER',
    },
    decimal: {
      valueControl: 'valueDecimal',
      toControl: 'valueDecimalTo',
      dataType: 'NUMBER',
    },
    date: {
      valueControl: 'valueDateFrom',
      toControl: 'valueDateTo',
      dataType: 'DATE',
    },
    logical: { valueControl: 'valueBoolean', dataType: 'VARCHAR2' },
  };

  private readonly valueControls = [
    'valueText',
    'valueInteger',
    'valueIntegerTo',
    'valueDecimal',
    'valueDecimalTo',
    'valueDateFrom',
    'valueDateTo',
    'valueBoolean',
  ];

  constructor() {
    super();
  }

  @HandleLoadingState({ loadingProperty: 'lookupLoading' })
  private async setClientFieldsFromCriteria(criteria: FilterCriteria[]) {
    for (const c of criteria) {
      const parts = c.tableAlias.split('_');
      const fieldId = Number(parts[parts.length - 1]);

      const field = this.clientFields.find((f) => f.id === fieldId);
      if (!field) continue;

      this.showField = true;
      const fg = this.createFieldFormGroup();

      fg.controls['fieldId'].setValue(fieldId, { emitEvent: false });
      fg.controls['fieldType'].setValue(field.fieldType, { emitEvent: false });
      fg.controls['operatorOptions'].setValue(
        this.getOperatorOptions(field.fieldType),
        { emitEvent: false },
      );
      fg.controls['operator'].enable({ emitEvent: false });
      fg.controls['operator'].setValue(c.operator, { emitEvent: false });
      // Logical rows must keep the operator locked once hydrated from a saved filter.
      if (field.fieldType?.toLowerCase() === 'logical') {
        fg.controls['operator'].disable({ emitEvent: false });
      }

      if (field.isLookup && field.fieldType?.toLowerCase() === 'character') {
        fg.controls['isLookup'].setValue(true, { emitEvent: false });
        const lookupValues =
          await this.api.filtersCriteria.getClientFieldLookup(
            this.contractNumber,
            field.id,
          );
        fg.controls['lookupOptions'].setValue(
          lookupValues.map((v) => ({ label: v, value: v })),
          { emitEvent: false },
        );
      }

      this.setClientFields(fg, field.fieldType, c.operator, c.values);
      this.updateValidation(fg, c.operator);
      this.setupFieldSubscriptions(fg);
      this.fgroups.controls.criterias.push(fg);
    }

    this.emitCount();
  }

  private setClientFields(
    fg: FormGroup,
    fieldType: string,
    operator: string,
    values: string[],
  ) {
    if (operator === 'ISNULL' || operator === 'ISNOTNULL' || !values.length) {
      return;
    }

    const val = values[0];
    switch (fieldType?.toLowerCase()) {
      case 'character':
        if (fg.controls['isLookup'].value) {
          const options = fg.controls['lookupOptions'].value;
          const matched = values
            .map((v) => {
              const opt = options.find(
                (o: any) => o.value.toUpperCase() === v.toUpperCase(),
              );
              return opt ? opt.value : v;
            })
            .filter(Boolean);
          fg.controls['valueText'].setValue(matched);
        } else {
          fg.controls['valueText'].setValue(val);
        }
        break;
      case 'logical':
        fg.controls['valueBoolean'].setValue(val);
        break;
      case 'integer':
        if (operator === 'BETWEEN') {
          const [from, to] = val.split(' AND ');
          fg.controls['valueInteger'].setValue(Number(from));
          fg.controls['valueIntegerTo'].setValue(Number(to));
        } else {
          fg.controls['valueInteger'].setValue(Number(val));
        }
        break;
      case 'decimal':
        if (operator === 'BETWEEN') {
          const [from, to] = val.split(' AND ');
          fg.controls['valueDecimal'].setValue(Number(from));
          fg.controls['valueDecimalTo'].setValue(Number(to));
        } else {
          fg.controls['valueDecimal'].setValue(Number(val));
        }
        break;
      case 'date':
        if (operator === 'BETWEEN') {
          const [fromStr, toStr] = val.split('|');
          fg.controls['valueDateFrom'].setValue(new Date(fromStr));
          fg.controls['valueDateTo'].setValue(new Date(toStr));
        } else {
          fg.controls['valueDateFrom'].setValue(new Date(val));
        }
        break;
    }
  }

  async ngOnInit() {
    this.section.criteria = this;

    const client = this.reports.selectedClient();
    if (client) {
      this.contractNumber = Number(client);
      await this.loadClientFields(this.contractNumber);
    }

    if (this.section.values?.length) {
      await this.setClientFieldsFromCriteria(this.section.values);
    }
  }

  @HandleLoadingState()
  private async loadClientFields(contractNumber: number) {
    this.clientFields =
      await this.api.filtersCriteria.getClientFields(contractNumber);
  }

  removeFieldCriteria(index: number) {
    this.fgroups.controls.criterias.removeAt(index);
    this.emitCount();
  }

  getFieldType(id: number) {
    const fieldType = this.clientFields.find((f) => f.id === id);
    return fieldType ? fieldType.fieldType : null;
  }

  clientFields: ClientFieldFilter[] = [];

  getAvailableFields(currentRowIndex: number): ClientFieldFilter[] {
    const selectedIds = this.fgroups.controls.criterias.controls
      .map((fg, index) =>
        index !== currentRowIndex ? fg.get('fieldId')?.value : null,
      )
      .filter((id) => id != null);

    return this.clientFields.filter((f) => !selectedIds.includes(f.id));
  }

  getOperatorOptions(fieldType: string | null) {
    switch (fieldType?.toLowerCase()) {
      case 'date':
        return [
          { label: 'After', value: 'GREATERTHAN' },
          { label: 'Before', value: 'LESSTHAN' },
          { label: 'On or after', value: 'GREATERTHANEQUALTO' },
          { label: 'On or before', value: 'LESSTHANEQUALTO' },
          { label: 'On', value: 'EQUALS' },
          { label: 'Between', value: 'BETWEEN' },
          { label: 'Is null', value: 'ISNULL' },
          { label: 'Is not null', value: 'ISNOTNULL' },
        ];
      case 'integer':
      case 'decimal':
        return [
          { label: 'Is equal to', value: 'EQUALS' },
          { label: 'Is not equal to', value: 'NOTEQUALTO' },
          { label: 'Greater than or equal to', value: 'GREATERTHANEQUALTO' },
          { label: 'Less than or equal to', value: 'LESSTHANEQUALTO' },
          { label: 'Between', value: 'BETWEEN' },
        ];
      case 'character':
        return [
          { label: 'Is equal to', value: 'EQUALS' },
          { label: 'Is not equal to', value: 'NOTEQUALTO' },
          { label: 'Contains', value: 'LIKE' },
          { label: 'Is null', value: 'ISNULL' },
          { label: 'Is not null', value: 'ISNOTNULL' },
        ];
      case 'logical':
        return [{ label: 'Is equal to', value: 'EQUALS' }];
      default:
        return [];
    }
  }

  fgroups = new FormGroup({
    criterias: new FormArray([]),
  });

  addNewFieldCriteria() {
    this.showField = true;
    const fg = this.createFieldFormGroup();
    this.setupFieldSubscriptions(fg);
    this.fgroups.controls.criterias.push(fg);
  }

  private createFieldFormGroup(): FormGroup {
    return new FormGroup({
      fieldType: new FormControl<string>(null),
      fieldId: new FormControl<number>(null),
      operator: new FormControl<string>({ value: null, disabled: true }),
      operatorOptions: new FormControl<any[]>([]),
      isLookup: new FormControl<boolean>(false),
      lookupOptions: new FormControl<any[]>([]),
      valueText: new FormControl<string>(null),
      valueInteger: new FormControl<number>(null),
      valueIntegerTo: new FormControl<number>(null),
      valueDecimal: new FormControl<number>(null),
      valueDecimalTo: new FormControl<number>(null),
      valueDateFrom: new FormControl<Date>(null),
      valueDateTo: new FormControl<Date>(null),
      valueBoolean: new FormControl<string>(null),
      empty: new FormControl<string>({ value: '', disabled: true }),
    });
  }

  private setupFieldSubscriptions(fg: FormGroup): void {
    this.dispose.subs = fg.controls['fieldId'].valueChanges.subscribe(
      async (value) => {
        if (value != null) {
          fg.controls['operator'].enable();
          const field = this.clientFields.find((f) => f.id === value);
          const fieldType = field?.fieldType ?? null;
          fg.controls['fieldType'].setValue(fieldType);
          fg.controls['operatorOptions'].setValue(
            this.getOperatorOptions(fieldType),
          );

          if (field?.isLookup && fieldType?.toLowerCase() === 'character') {
            this.lookupLoading = true;
            try {
              fg.controls['isLookup'].setValue(true);
              const lookupValues =
                await this.api.filtersCriteria.getClientFieldLookup(
                  this.contractNumber,
                  field.id,
                );
              fg.controls['lookupOptions'].setValue(
                lookupValues.map((v) => ({ label: v, value: v })),
              );
            } finally {
              this.lookupLoading = false;
            }
          } else {
            fg.controls['isLookup'].setValue(false);
            fg.controls['lookupOptions'].setValue([]);
          }

          // Logical fields expose a single operator (Is equal to) that is applied
          // automatically and is not user-editable. All other field types start
          // with the operator empty so the user can choose one.
          if (fieldType?.toLowerCase() === 'logical') {
            fg.controls['operator'].setValue('EQUALS');
            fg.controls['operator'].disable();
          } else {
            fg.controls['operator'].setValue(null);
          }
          this.clearValues(fg);
        } else {
          fg.controls['operator'].disable();
          fg.controls['fieldType'].setValue(null);
          fg.controls['operator'].setValue(null);
          fg.controls['operatorOptions'].setValue([]);
          fg.controls['isLookup'].setValue(false);
          fg.controls['lookupOptions'].setValue([]);
        }
      },
    );

    this.dispose.subs = fg.controls['operator'].valueChanges.subscribe(
      (operator) => {
        this.updateValidation(fg, operator);
        this.emitCount();
      },
    );

    this.dispose.subs = fg.valueChanges.subscribe(() => this.emitCount());

    this.dispose.subs = fg.controls['valueDateFrom'].valueChanges.subscribe(
      () => {
        fg.controls['valueDateTo'].updateValueAndValidity({
          emitEvent: false,
        });
      },
    );
    this.dispose.subs = fg.controls['valueDateTo'].valueChanges.subscribe(
      () => {
        fg.controls['valueDateFrom'].updateValueAndValidity({
          emitEvent: false,
        });
      },
    );
    this.dispose.subs = fg.controls['valueInteger'].valueChanges.subscribe(
      () => {
        fg.controls['valueIntegerTo'].updateValueAndValidity({
          emitEvent: false,
        });
      },
    );
    this.dispose.subs = fg.controls['valueIntegerTo'].valueChanges.subscribe(
      () => {
        fg.controls['valueInteger'].updateValueAndValidity({
          emitEvent: false,
        });
      },
    );
    this.dispose.subs = fg.controls['valueDecimal'].valueChanges.subscribe(
      () => {
        fg.controls['valueDecimalTo'].updateValueAndValidity({
          emitEvent: false,
        });
      },
    );
    this.dispose.subs = fg.controls['valueDecimalTo'].valueChanges.subscribe(
      () => {
        fg.controls['valueDecimal'].updateValueAndValidity({
          emitEvent: false,
        });
      },
    );
  }

  private clearValues(fg: FormGroup): void {
    const isLookup = fg.controls['isLookup'].value;
    fg.controls['valueText'].setValue(isLookup ? [] : null, {
      emitEvent: false,
    });
    fg.controls['valueInteger'].setValue(null, { emitEvent: false });
    fg.controls['valueIntegerTo'].setValue(null, { emitEvent: false });
    fg.controls['valueDecimal'].setValue(null, { emitEvent: false });
    fg.controls['valueDecimalTo'].setValue(null, { emitEvent: false });
    fg.controls['valueDateFrom'].setValue(null, { emitEvent: false });
    fg.controls['valueDateTo'].setValue(null, { emitEvent: false });
    fg.controls['valueBoolean'].setValue(null, { emitEvent: false });
  }

  private updateValidation(fg: FormGroup, operator: string): void {
    const fieldType = fg.controls['fieldType'].value?.toLowerCase();

    // Clear all validators first
    this.clearAllValidators(fg);

    // Null operators don't need value validation
    if (!operator || operator === 'ISNULL' || operator === 'ISNOTNULL') {
      this.updateAllValidity(fg);
      return;
    }

    switch (fieldType) {
      case 'character':
        fg.controls['valueText'].addValidators([Validators.required]);
        fg.controls['valueText'].markAsTouched();
        fg.controls['valueText'].markAsDirty();
        break;

      case 'integer':
        fg.controls['valueInteger'].addValidators([Validators.required]);
        fg.controls['valueInteger'].markAsTouched();
        fg.controls['valueInteger'].markAsDirty();
        if (operator === 'BETWEEN') {
          fg.controls['valueInteger'].addValidators([
            ValidatorsEx.integerLessThan(
              fg.controls['valueIntegerTo'],
              false,
              'From value must be less than To value',
            ),
          ]);
          fg.controls['valueIntegerTo'].addValidators([
            Validators.required,
            ValidatorsEx.integerGreaterThan(
              fg.controls['valueInteger'],
              false,
              'To value must be greater than From value',
            ),
          ]);
          fg.controls['valueIntegerTo'].markAsTouched();
          fg.controls['valueIntegerTo'].markAsDirty();
        }
        break;

      case 'decimal':
        fg.controls['valueDecimal'].addValidators([Validators.required]);
        fg.controls['valueDecimal'].markAsTouched();
        fg.controls['valueDecimal'].markAsDirty();
        if (operator === 'BETWEEN') {
          fg.controls['valueDecimal'].addValidators([
            ValidatorsEx.decimalLessThan(
              fg.controls['valueDecimalTo'],
              false,
              'From value must be less than To value',
            ),
          ]);
          fg.controls['valueDecimalTo'].addValidators([
            Validators.required,
            ValidatorsEx.decimalGreaterThan(
              fg.controls['valueDecimal'],
              false,
              'To value must be greater than From value',
            ),
          ]);
          fg.controls['valueDecimalTo'].markAsTouched();
          fg.controls['valueDecimalTo'].markAsDirty();
        }
        break;

      case 'date':
        fg.controls['valueDateFrom'].addValidators([Validators.required]);
        fg.controls['valueDateFrom'].markAsTouched();
        fg.controls['valueDateFrom'].markAsDirty();
        if (operator === 'BETWEEN') {
          fg.controls['valueDateFrom'].addValidators([
            ValidatorsEx.dateLessThan(
              fg.controls['valueDateTo'],
              false,
              'From date must be earlier than To date',
            ),
          ]);
          fg.controls['valueDateTo'].addValidators([
            Validators.required,
            ValidatorsEx.dateGreaterThan(
              fg.controls['valueDateFrom'],
              false,
              'To date must be later than From date',
            ),
          ]);
          fg.controls['valueDateTo'].markAsTouched();
          fg.controls['valueDateTo'].markAsDirty();
        }
        break;

      case 'logical':
        fg.controls['valueBoolean'].addValidators([Validators.required]);
        fg.controls['valueBoolean'].markAsTouched();
        fg.controls['valueBoolean'].markAsDirty();
        break;
    }

    this.updateAllValidity(fg);
  }

  private clearAllValidators(fg: FormGroup): void {
    this.valueControls.forEach((name) => fg.controls[name].clearValidators());
  }

  private updateAllValidity(fg: FormGroup): void {
    this.valueControls.forEach((name) =>
      fg.controls[name].updateValueAndValidity({ emitEvent: false }),
    );
  }

  deleteAll() {
    this.showField = false;
    this.fgroups.controls.criterias = new FormArray([]);
    this.emitCount();
  }

  clearCriteria(): void {
    this.showField = false;
    this.fgroups.controls.criterias = new FormArray([]);
    this.section?.count.set(0);
  }

  private emitCount(): void {
    const count = this.fgroups.controls.criterias.controls.filter((fg) =>
      this.isCompleteCriteria(fg),
    ).length;
    this.section?.count.set(count);
  }

  private isCompleteCriteria(fg: AbstractControl): boolean {
    const fieldId = fg.get('fieldId')?.value;
    const operator = fg.get('operator')?.value;
    if (fieldId == null || !operator) return false;
    if (operator === 'ISNULL' || operator === 'ISNOTNULL') return true;
    return this.hasValue(fg as FormGroup) && fg.valid;
  }

  private hasValue(fg: FormGroup): boolean {
    const fieldType = fg.get('fieldType')?.value?.toLowerCase();
    const operator = fg.get('operator')?.value;
    const config = this.fieldConfig[fieldType];
    if (!config) return false;

    const val = fg.get(config.valueControl)?.value;
    const hasFrom = Array.isArray(val) ? val.length > 0 : val != null;
    if (operator !== 'BETWEEN' || !config.toControl) return hasFrom;
    return hasFrom && fg.get(config.toControl)?.value != null;
  }

  valid(): boolean {
    return this.fgroups.valid;
  }

  getCriteria(): FilterCriteria[] {
    return this.fgroups.controls.criterias.controls
      .filter((fg) => this.isCompleteCriteria(fg))
      .map((fg) => {
        const fieldId = fg.get('fieldId')?.value.toString().padStart(4, '0');
        const contractNumber = this.contractNumber.toString().padStart(4, '0');
        const fieldType = fg.get('fieldType')?.value;
        const operator = fg.get('operator')?.value;

        return {
          tableAlias: `ccf_${contractNumber}_${fieldId}`,
          column: 'COLUMN_VALUE',
          displayName: '',
          operator: operator,
          values: this.getValues(fg as FormGroup),
          lineOfBusiness: '',
          dataType: this.getDataType(fieldType),
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Client Field',
          filterSubsection: '',
        } as FilterCriteria;
      });
  }

  private getDataType(fieldType: string): string {
    return this.fieldConfig[fieldType?.toLowerCase()]?.dataType ?? 'VARCHAR2';
  }

  private getValues(fg: FormGroup): string[] {
    const fieldType = fg.get('fieldType')?.value?.toLowerCase();
    const operator = fg.get('operator')?.value;

    if (operator === 'ISNULL' || operator === 'ISNOTNULL') {
      return [];
    }

    const formatDate = (date: Date): string => {
      if (!date) return '';
      return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
    };

    switch (fieldType) {
      case 'character': {
        const val = fg.get('valueText')?.value;
        if (Array.isArray(val)) {
          return val;
        }
        return [val ?? ''];
      }
      case 'integer':
        if (operator === 'BETWEEN') {
          return [
            `${fg.get('valueInteger')?.value} and ${fg.get('valueIntegerTo')?.value}`,
          ];
        }
        return [fg.get('valueInteger')?.value?.toString() ?? ''];
      case 'decimal':
        if (operator === 'BETWEEN') {
          return [
            `${fg.get('valueDecimal')?.value} and ${fg.get('valueDecimalTo')?.value}`,
          ];
        }
        return [fg.get('valueDecimal')?.value?.toString() ?? ''];
      case 'date':
        if (operator === 'BETWEEN') {
          return [
            `${formatDate(fg.get('valueDateFrom')?.value)}|${formatDate(fg.get('valueDateTo')?.value)}`,
          ];
        }
        return [formatDate(fg.get('valueDateFrom')?.value)];
      case 'logical':
        return [fg.get('valueBoolean')?.value ?? ''];
      default:
        return [];
    }
  }
}
